import type { SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'
import type { ScanEventDescription } from './fh-vitals-events'
import type {
  MindsetDemographics,
  MindsetPhase,
  MindsetSessionConfiguration,
} from './mindset-session'
import {
  measureMindsetSession,
  observeMindsetClient,
  prepareMindsetSession,
} from './mindset-session'
import { toSubmitRequestFromMindset } from './mindset-result'
import type { MindsetStopPayload, MindsetVitalClient } from './mindset-types'
import { ELAPSED, raceElapsed } from './session-timeouts'
import { toSubmitRequestFromShenAi } from './shenai-result'
import type { ShenAiSessionConfiguration } from './shenai-session'
import { pollShenAiUntilFinished, prepareShenAiSession } from './shenai-session'
import type { ShenAiMeasurementResults, ShenAiVitalClient } from './shenai-types'

/**
 * The phase vocabulary is the vendors' own, imported rather than restated - the same argument
 * shenai-session.ts makes about reusing it: what the patient is waiting for is a download, a
 * licence, a measurement, and that does not become a different set of things because two SDKs are
 * doing it.
 */
export type HybridPhase = MindsetPhase

/**
 * Both vendors' own configurations, side by side and unchanged.
 *
 * NOT flattened into one set of timeouts. The two SDKs are bounded by different numbers for
 * different reasons - Mindset shares one budget across init and ready (docs/decisions.md D-27),
 * Shen.AI splits its download from its licence call (D-34) - and a single `readyTimeoutMs` here
 * would be this file quietly overruling both of those arguments.
 */
export interface HybridSessionConfiguration {
  readonly mindset: MindsetSessionConfiguration
  readonly shenAi: ShenAiSessionConfiguration
  /**
   * The hard cap on how long the patient is asked to hold still, across BOTH halves, measured from
   * the moment the first engine starts measuring (docs/decisions.md D-42).
   *
   * It bounds the SECOND half in practice: the first vendor's window is its own preset's, and
   * whatever is left of this budget when that finishes is what the second vendor gets. When it runs
   * out the patient is released whether or not the second vendor produced anything - by that point
   * the platform's reading has been submitted for a minute, and the person holding the phone is
   * waiting on a side channel.
   */
  readonly holdWindowMs: number
  /**
   * Which of Mindset's vitals the CONCURRENT runner asks for - pulse, respiration and pressure when absent.
   * A diagnostic narrowing for a local stack (runtime-config.ts `hybridMindsetVitals`): pressure is the one
   * that brings up a second ONNX Runtime heap inside Mindset's worker, and an iPhone that cannot hold both
   * vendors measuring is asked here which of them tips it over (docs/decisions.md D-62).
   */
  readonly mindsetVitalFamilies?: readonly MindsetVitalFamily[]
}

/** A Mindset vital without its model suffix: `PR` covers `PR_MD` and `PR_GW`. */
export type MindsetVitalFamily = 'PR' | 'RR' | 'BP'

/** The credential the Shen.AI half runs on, minted by this service (docs/decisions.md D-32). */
export interface ShenAiCredentials {
  readonly token: string
  /** The installation's single vendor-side identity, never the patient's. */
  readonly userId: string
}

export type HybridSessionOutcome =
  | { readonly outcome: 'measured'; readonly request: SubmitScanRequest }
  | { readonly outcome: 'no-usable-result' }
  | { readonly outcome: 'error'; readonly messageKey: TranslationKey }

/** Why the second half ended. It ends for one of exactly three reasons, and the log says which. */
type SecondHalfEnding =
  | 'the vendor settled its heart rate'
  | 'the vendor stopped itself'
  | 'the hold window closed'

/**
 * The vitals this scan asks Mindset for: pulse and respiration (docs/decisions.md D-38, D-39).
 *
 * Blood pressure is not among them and neither is blood oxygen. Naming `BP_*` loads a whole ONNX
 * model and runs an inference per frame for a number this scan does not read - the platform's
 * pressure came from the OTHER vendor, in the first half, and has already been submitted by the time
 * this vendor starts.
 *
 * Matched on the PREFIX, because every vital is emitted by two model families - `_MD` clinical and
 * `_GW` general-wellness - and which pair a clinic gets is its own entitlement (mindset-result.ts).
 */
const MINDSET_VITALS_THIS_SCAN_READS = /^(PR|RR)_/

/**
 * Runs BOTH SDKs over ONE camera, ONE AFTER THE OTHER, in a single unbroken session
 * (docs/decisions.md D-42).
 *
 * **This was concurrent, and a phone would not have it.** D-37 ran both engines over the same frames
 * and composed a reading from the two; D-38 through D-40 trimmed everything that could be trimmed - a
 * per-frame log that leaked, a blood-pressure model nobody read, half the frame rate, half the
 * measurement, four metrics instead of nine - and a real iPhone still killed the tab, flakily, about
 * two runs in three. Two WASM heaps beside a WebGL2 pipeline is more than WebKit's per-tab ceiling
 * allows, and no amount of trimming closes a gap that wide. So the two engines no longer overlap at
 * all, and everything below follows from that one change.
 *
 * The order, and the reason for each step:
 *
 * 1. **Shen.AI goes first and alone, and its reading is the platform's.** It measures every value
 *    `SubmitScanRequest` carries except blood oxygen - which it does not measure and this clinic is
 *    not licensed for - so a thirty-second first half produces a complete reading rather than half of
 *    one. That is what makes it the half worth having first.
 * 2. **The reading is submitted the moment it exists**, through `onReadingReady`, before the second
 *    vendor is so much as loaded. Everything after that point is a side channel: if the tab dies, if
 *    the second SDK never loads, if the patient walks away - the assessment is already `Completed` and
 *    the measurement is already stored. That ordering is what makes the rest of this safe rather than
 *    merely shorter.
 * 3. **The first runtime is handed back before the second is loaded** - `destroy()` and then
 *    `releaseRuntime()`, which is the `destroyRuntime()` call D-35 refused. If it traps, it traps
 *    after the submit.
 * 4. **The patient is NOT released.** They stay on the scanning screen, on one continuous `measuring`
 *    phase, because releasing them here is what would make the second measurement a reading of a
 *    ceiling.
 * 5. **They are released when the VENDOR says its heart rate has settled** - its own `S-HRT-061` -
 *    or when the hold window closes, whichever comes first.
 * 6. **Nothing Mindset measures is submitted.** It is logged in full and handed to the vendor's own
 *    records where this installation forwards them. A second submit is a server-side change nobody
 *    has made yet, and this runner does not pretend otherwise.
 *
 * Both clients are injected, for the reason each single-vendor runner gives about its own: a scan
 * needs a camera, two WASM modules and a real minute, and none of those belong in a unit test.
 */
export async function runHybridScanSession(
  mindsetClient: MindsetVitalClient,
  shenAiClient: ShenAiVitalClient,
  demographics: MindsetDemographics,
  shenAiCredentials: ShenAiCredentials,
  config: HybridSessionConfiguration,
  /**
   * Every signal whichever vendor is currently measuring is raising.
   *
   * Nothing is merged any more and there is nothing to merge: one engine measures at a time, so the
   * signals on screen are that engine's. Deduplication within a vendor is still each describer's own
   * job (mindset-events.ts, shenai-events.ts).
   */
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  /**
   * How far through the hold the patient is, as a fraction of one.
   *
   * **The two halves own half the bar each.** Shen.AI's own progress drives 0 to 0.5 and Mindset's
   * drives 0.5 to 1. Forwarding either vendor's percentage raw would run the bar to full at the
   * halfway point and then start it again, which reads as the scan restarting - and the whole point
   * of this arrangement is that the patient cannot tell where one SDK ends and the other begins.
   */
  onProgress?: (fractionComplete: number) => void,
  /**
   * Which phase to show.
   *
   * `loading` and `licensing` are reported for the FIRST half only. Once measurement has begun the
   * phase stays `measuring` for the rest of the scan, including through the second vendor's own
   * download - because that is what the PATIENT is doing: still sitting still, still being asked to.
   * "Getting the scanner ready" reappearing mid-scan reads as a restart, and the instruction on
   * screen has to stay true.
   */
  onPhase?: (phase: HybridPhase) => void,
  /**
   * The platform's reading, as soon as the first vendor produces it.
   *
   * A CALLBACK rather than the return value, because the return value arrives a minute later and the
   * submit must not wait for the second vendor. The caller owns retrying it; this runner never learns
   * whether it succeeded, because there is nothing it would do differently either way.
   */
  onReadingReady?: (request: SubmitScanRequest) => void,
  /**
   * Mindset's own result, for this installation's forward to the vendor's records (D-33).
   *
   * Called for a session that produced anything at all, usable or not - a reading this app cannot map
   * is still the vendor's own measurement.
   */
  onMindsetVendorResult?: (result: unknown) => void,
): Promise<HybridSessionOutcome> {
  const phase = (next: HybridPhase) => {
    console.log('[hybrid] phase', next)
    onPhase?.(next)
  }

  const firstHalfProgress = (fraction: number) => onProgress?.(Math.min(0.5, fraction / 2))
  const secondHalfProgress = (fraction: number) => onProgress?.(0.5 + Math.min(0.5, fraction / 2))

  try {
    // ---- The first half: Shen.AI, alone ---------------------------------------------------------
    const shenAi = await prepareShenAiSession(
      shenAiClient,
      shenAiCredentials.token,
      shenAiCredentials.userId,
      config.shenAi,
      firstHalfProgress,
      phase,
    )

    if (!shenAi.prepared) {
      console.warn('[hybrid] the first vendor could not get ready, so nothing measured')
      return { outcome: 'error', messageKey: shenAi.messageKey }
    }

    const startedHolding = Date.now()
    const heldFor = () => Date.now() - startedHolding
    const remainingHold = () => config.holdWindowMs - heldFor()

    phase('measuring')
    shenAiClient.startMeasurement()

    const finishedItself = await pollShenAiUntilFinished(
      shenAiClient,
      config.shenAi,
      onSignals,
      firstHalfProgress,
    )

    if (!finishedItself) {
      // The window closed with the vendor still measuring. Stopping is what makes it finalize
      // whatever it has, which is often a usable reading - so the result is read either way.
      shenAiClient.stopMeasurement()
    }

    const shenAiResults: ShenAiMeasurementResults | null = shenAiClient.results()

    console.log('[hybrid] first half: shenai raw vendor result', shenAiResults)

    const request = toSubmitRequestFromShenAi(shenAiResults)
    console.log('[hybrid] first half: the platform reading', request ?? null, {
      afterMs: heldFor(),
    })

    if (!request) {
      // No reading, so there is nothing to submit and nothing for a side channel to be beside. The
      // patient is offered another go rather than held for another minute on a scan whose measurement
      // has already failed.
      return { outcome: 'no-usable-result' }
    }

    // SUBMITTED HERE, a minute before this function returns. Everything past this line is a side
    // channel that cannot cost the patient their reading.
    onReadingReady?.(request)

    // ---- The handover: give the first runtime back before loading the second --------------------
    //
    // In this order, and both guarded. `destroy()` stops the pipeline and this vendor's own camera
    // clone; `releaseRuntime()` hands the 33 MB heap back (shenai-types.ts records why that call is
    // dangerous and why it is made anyway). A throw in either is reported and stepped over: the
    // reading is already submitted, and the worst case is a second half that cannot load.
    try {
      shenAiClient.destroy()
    } catch (error) {
      console.warn('[hybrid] the first vendor threw while being torn down', error)
    }
    try {
      shenAiClient.releaseRuntime?.()
    } catch (error) {
      console.warn('[hybrid] the first vendor threw while releasing its runtime', error)
    }

    // ---- The second half: Mindset, for its own records ------------------------------------------
    if (remainingHold() <= 0) {
      console.log('[hybrid] the hold window was already spent, so the second half did not run')
      return { outcome: 'measured', request }
    }

    console.log('[hybrid] second half: starting mindset', { remainingHoldMs: remainingHold() })

    const observation = observeMindsetClient(mindsetClient, onSignals, secondHalfProgress)

    // The phase is deliberately NOT reported through this vendor's own prologue: `prepareMindsetSession`
    // is given no `onPhase`, so the screen stays on `measuring` while 55 MB comes down (see onPhase).
    const prepared = await raceElapsed(
      remainingHold(),
      prepareMindsetSession(mindsetClient, observation, config.mindset),
    )

    if (prepared === ELAPSED) {
      console.warn('[hybrid] the hold window closed before the second vendor was ready', {
        afterMs: heldFor(),
      })
      return { outcome: 'measured', request }
    }

    if (!prepared.prepared) {
      console.warn('[hybrid] the second vendor could not get ready', { reason: prepared.messageKey })
      return { outcome: 'measured', request }
    }

    const vitals = prepared.vitals.filter((vital) => MINDSET_VITALS_THIS_SCAN_READS.test(vital))

    console.log('[hybrid] second half: mindset vitals', {
      licensed: prepared.vitals,
      measuring: vitals,
    })

    if (vitals.length === 0) {
      console.warn('[hybrid] the second vendor is licensed for nothing this scan reads')
      return { outcome: 'measured', request }
    }

    // STARTED AND THEN WATCHED, rather than awaited to completion: the patient is released on the
    // vendor's own heart-rate signal, which arrives long before a session of its own accord would end
    // (its length is the licence's, not ours - docs/decisions.md D-40).
    const measuring = measureMindsetSession(mindsetClient, observation, vitals, demographics, {
      ...config.mindset,
      measureWindowMs: Math.max(0, remainingHold()),
    })

    const ending = await whicheverComesFirst(remainingHold(), observation.hrSettled, measuring)

    console.log('[hybrid] second half ended because', ending, { afterMs: heldFor() })

    // Whatever the vendor has RIGHT NOW, which is the point of releasing on its own signal: a settled
    // heart rate is a result worth reading, and reading it is what `stop()` does.
    const stopped = observation.stopped() ?? (await stopQuietly(mindsetClient))

    if (stopped?.result) {
      onMindsetVendorResult?.(stopped.result)
    }

    // NOT SUBMITTED, and that is the shape of the second half today: the platform's reading was the
    // other vendor's, a minute ago. A second submit is a server-side change nobody has made yet, so
    // this vendor's numbers exist in exactly two places - its own records, where this installation
    // forwards them, and this console.
    console.log('[hybrid] second half: mindset raw vendor result', stopped?.result)
    console.log('[hybrid] second half: mindset against what was submitted', {
      mindsetWouldHaveSubmitted:
        toSubmitRequestFromMindset(stopped?.result?.finalVitalsMeasurementValues) ?? null,
      actuallySubmitted: request,
    })

    return { outcome: 'measured', request }
  } catch (error) {
    console.warn('[hybrid] the session threw', error)
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  } finally {
    // BOTH, each in its own try. The first vendor has usually been torn down already, mid-run, and a
    // second `destroy()` on a client with nothing left to give is a no-op; what must not happen is one
    // vendor's teardown taking the other's with it, or a camera track outliving the page (D-24).
    try {
      shenAiClient.destroy()
    } catch {
      // Already reported where it mattered.
    }
    try {
      mindsetClient.destroy()
    } catch {
      // Nothing left to salvage from this one either.
    }
  }
}

/**
 * Whichever of three things happens first: the vendor settling its heart rate, the vendor ending its
 * own session, or the hold window closing.
 *
 * The reasons are returned by NAME rather than as a boolean, because all three are normal and the
 * console has to say which - "the scan produced nothing" has too many indistinguishable causes in
 * this integration already (docs/decisions.md D-35).
 */
async function whicheverComesFirst(
  windowMs: number,
  hrSettled: Promise<void>,
  measuring: Promise<MindsetStopPayload | null>,
): Promise<SecondHalfEnding> {
  return Promise.race<SecondHalfEnding>([
    hrSettled.then(() => 'the vendor settled its heart rate' as const),
    measuring.then(() => 'the vendor stopped itself' as const),
    new Promise<SecondHalfEnding>((resolve) =>
      setTimeout(() => resolve('the hold window closed'), Math.max(0, windowMs)),
    ),
  ])
}

/** `stop()`, for a caller that has already decided the scan is over whatever the vendor answers. */
async function stopQuietly(client: MindsetVitalClient): Promise<MindsetStopPayload | null> {
  try {
    return await client.stop()
  } catch (error) {
    console.warn('[hybrid] the second vendor threw while being stopped', error)
    return null
  }
}
